import React from 'react';

/**
 * Das eine Icon-Set der Anwendung: feine Linien-Icons, alle im selben Strich
 * (1,8 px, runde Enden), gefärbt über `currentColor`. Statt Emojis — die sehen
 * auf jedem Gerät anders aus, sind bunt, wo die Oberfläche ruhig sein soll, und
 * lesen sich in Screenreadern als „Gesicht mit Tränen der Freude“.
 *
 * Größe über `size` (Pixel, Standard 16) oder `className`. Ein neues Icon kommt
 * hierher, nicht als Inline-SVG in eine Seite.
 */
export interface IconProps {
  size?: number;
  className?: string;
  title?: string;
  style?: React.CSSProperties;
}

const svg = (inhalt: React.ReactNode): React.FC<IconProps> => {
  const Komponente: React.FC<IconProps> = ({ size = 16, className, title, style }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`ui-icon ${className ?? ''}`}
      style={style}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      focusable="false"
    >
      {title && <title>{title}</title>}
      {inhalt}
    </svg>
  );
  return Komponente;
};

export const Icon = {
  // Allgemein
  Plus: svg(<path d="M12 5v14M5 12h14" />),
  Search: svg(<><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></>),
  Close: svg(<path d="M18 6L6 18M6 6l12 12" />),
  Check: svg(<path d="M20 6L9 17l-5-5" />),
  Edit: svg(<path d="M12 20h9M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4 12.5-12.5z" />),
  Trash: svg(<path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m2 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z" />),
  Download: svg(<path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />),
  Upload: svg(<path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />),
  ImportExport: svg(<path d="M7 4v16M3 8l4-4 4 4M17 20V4M13 16l4 4 4-4" />),
  Refresh: svg(<path d="M20.5 12a8.5 8.5 0 11-2.6-6.1L20.5 8.5M20.5 3.5v5h-5" />),
  Eye: svg(<><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" /><circle cx="12" cy="12" r="3" /></>),
  EyeOff: svg(<path d="M17.9 17.9A10.9 10.9 0 0112 19c-7 0-11-7-11-7a19.6 19.6 0 015.1-5.9M9.9 5.2A10 10 0 0112 5c7 0 11 7 11 7a19.7 19.7 0 01-2.2 3.2M14.1 14.1a3 3 0 01-4.2-4.2M1 1l22 22" />),
  Link: svg(<path d="M10 13a5 5 0 007.5.5l3-3a5 5 0 00-7-7l-1.7 1.7M14 11a5 5 0 00-7.5-.5l-3 3a5 5 0 007 7l1.7-1.7" />),
  External: svg(<path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" />),
  Copy: svg(<><rect x="9" y="9" width="12" height="12" rx="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" /></>),
  Filter: svg(<path d="M22 3H2l8 9.5V19l4 2v-8.5L22 3z" />),
  Sort: svg(<path d="M7 4v16M3 16l4 4 4-4M17 20V4M13 8l4-4 4 4" />),
  Swap: svg(<path d="M4 8h14M14 4l4 4-4 4M20 16H6M10 12l-4 4 4 4" />),
  More: svg(<><circle cx="5" cy="12" r="1" /><circle cx="12" cy="12" r="1" /><circle cx="19" cy="12" r="1" /></>),
  Save: svg(<><path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" /><path d="M17 21v-8H7v8M7 3v5h8" /></>),
  Print: svg(<><path d="M6 9V2h12v7M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></>),

  // Richtung
  ChevronLeft: svg(<path d="M15 18l-6-6 6-6" />),
  ChevronRight: svg(<path d="M9 18l6-6-6-6" />),
  ChevronUp: svg(<path d="M18 15l-6-6-6 6" />),
  ChevronDown: svg(<path d="M6 9l6 6 6-6" />),
  ArrowLeft: svg(<path d="M19 12H5M11 18l-6-6 6-6" />),
  ArrowRight: svg(<path d="M5 12h14M13 6l6 6-6 6" />),
  ArrowUp: svg(<path d="M12 19V5M6 11l6-6 6 6" />),
  ArrowDown: svg(<path d="M12 5v14M18 13l-6 6-6-6" />),

  // Hinweise
  Warning: svg(<path d="M10.3 3.9L1.8 18a2 2 0 001.7 3h17a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0zM12 9v4M12 17h.01" />),
  Info: svg(<><circle cx="12" cy="12" r="9" /><path d="M12 16v-4M12 8h.01" /></>),
  CheckCircle: svg(<><circle cx="12" cy="12" r="9" /><path d="M8 12l3 3 5-6" /></>),
  XCircle: svg(<><circle cx="12" cy="12" r="9" /><path d="M15 9l-6 6M9 9l6 6" /></>),
  Lightbulb: svg(<path d="M9 18h6M10 22h4M12 2a7 7 0 00-4 12.7V16h8v-1.3A7 7 0 0012 2z" />),
  Lock: svg(<><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 018 0v4" /></>),

  // Dinge
  Calendar: svg(<><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></>),
  Clock: svg(<><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>),
  MapPin: svg(<><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0116 0z" /><circle cx="12" cy="10" r="3" /></>),
  Map: svg(<path d="M9 4L3 6v14l6-2 6 2 6-2V4l-6 2-6-2zM9 4v14M15 6v14" />),
  Route: svg(<><circle cx="6" cy="19" r="2.5" /><circle cx="18" cy="5" r="2.5" /><path d="M8.5 19H17a3.5 3.5 0 000-7H7a3.5 3.5 0 010-7h8.5" /></>),
  Car: svg(<><path d="M5 17H3v-5l2-5h14l2 5v5h-2" /><circle cx="7.5" cy="17" r="2" /><circle cx="16.5" cy="17" r="2" /><path d="M9.5 17h5M3 12h18" /></>),
  Target: svg(<><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1" /></>),
  Crosshair: svg(<><circle cx="12" cy="12" r="7" /><circle cx="12" cy="12" r="2.5" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3" /></>),
  Layers: svg(<path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />),
  Phone: svg(<path d="M22 16.9v3a2 2 0 01-2.2 2 19.8 19.8 0 01-8.6-3.1 19.5 19.5 0 01-6-6A19.8 19.8 0 012.1 4.2 2 2 0 014.1 2h3a2 2 0 012 1.7c.1.9.4 1.8.7 2.7a2 2 0 01-.5 2.1L8 9.8a16 16 0 006 6l1.3-1.3a2 2 0 012.1-.4c.9.3 1.8.6 2.7.7a2 2 0 011.7 2z" />),
  Mail: svg(<><rect x="2" y="4" width="20" height="16" rx="2" /><path d="M22 6l-10 7L2 6" /></>),
  Chat: svg(<path d="M21 11.5a8.4 8.4 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.4 8.4 0 01-3.8-.9L3 21l1.9-5.7a8.4 8.4 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.4 8.4 0 013.8-.9h.5a8.5 8.5 0 018 8v.5z" />),
  Note: svg(<><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><path d="M14 2v6h6M8 13h8M8 17h5" /></>),
  Doc: svg(<><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><path d="M14 2v6h6" /></>),
  Paperclip: svg(<path d="M21.4 11.1l-9.2 9.2a6 6 0 01-8.5-8.5l9.2-9.2a4 4 0 015.7 5.7l-9.2 9.2a2 2 0 01-2.8-2.8l8.5-8.5" />),
  Book: svg(<><path d="M4 5a2 2 0 0 1 2-2h13v16H6a2 2 0 0 0-2 2V5z" /><path d="M4 19a2 2 0 0 1 2-2h13" /><path d="M9 7h6" /></>),
  Newspaper: svg(<><path d="M4 22h16a2 2 0 002-2V4a2 2 0 00-2-2H8a2 2 0 00-2 2v16a2 2 0 01-2 2zm0 0a2 2 0 01-2-2v-9c0-1.1.9-2 2-2h2" /><path d="M18 14h-8M15 18h-5M10 6h8v4h-8z" /></>),
  Globe: svg(<><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a14 14 0 010 18M12 3a14 14 0 000 18" /></>),
  Chart: svg(<path d="M3 3v18h18M7 15l4-4 3 3 5-6" />),
  Euro: svg(<path d="M17 5.5A7 7 0 007 12a7 7 0 0010 6.5M4 10h9M4 14h9" />),
  Star: svg(<path d="M12 2l3.1 6.3 6.9 1-5 4.9 1.2 6.8L12 17.8 5.8 21l1.2-6.8-5-4.9 6.9-1L12 2z" />),
  Heart: svg(<path d="M20.8 4.6a5.5 5.5 0 00-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 00-7.8 7.8l1 1.1L12 21l7.8-7.5 1-1.1a5.5 5.5 0 000-7.8z" />),
  Key: svg(<><circle cx="7.5" cy="15.5" r="4" /><path d="M10.4 12.6L20 3M16.5 6.5l2.5 2.5M14 9l2 2" /></>),

  // Bereiche und Navigation
  Dashboard: svg(<><rect x="3" y="3" width="7" height="9" rx="1.5" /><rect x="14" y="3" width="7" height="5" rx="1.5" /><rect x="14" y="12" width="7" height="9" rx="1.5" /><rect x="3" y="16" width="7" height="5" rx="1.5" /></>),
  Users: svg(<><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20a6.5 6.5 0 0113 0M16 4.6a3.5 3.5 0 010 6.8M18 14.2a6.5 6.5 0 013.5 5.8" /></>),
  User: svg(<><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0116 0" /></>),
  UserPlus: svg(<><circle cx="9" cy="8" r="4" /><path d="M1.5 21a7.5 7.5 0 0115 0M19 8v6M16 11h6" /></>),
  Family: svg(<><circle cx="7" cy="7" r="2.5" /><circle cx="17" cy="7" r="2.5" /><circle cx="12" cy="13" r="2" /><path d="M2.5 20v-3a4.5 4.5 0 019 0M12.5 17a4.5 4.5 0 019 0v3M9 21a3 3 0 016 0" /></>),
  Home: svg(<path d="M3 10.5L12 3l9 7.5M5 9v11a1 1 0 001 1h4v-6h4v6h4a1 1 0 001-1V9" />),
  Building: svg(<path d="M4.5 21V4a1 1 0 011-1h13a1 1 0 011 1v17M3 21h18M8.5 7h2M13.5 7h2M8.5 11h2M13.5 11h2M8.5 15h2M13.5 15h2M10.5 21v-2.5h3V21" />),
  Hospital: svg(<><path d="M4 21V5a2 2 0 012-2h12a2 2 0 012 2v16M2 21h20M9.5 21v-4h5v4" /><path d="M12 7v5M9.5 9.5h5" /></>),
  Broom: svg(<path d="M19.5 2.5l-7.5 7.5M9.5 9l5.5 5.5M9.5 9L4 14.5c-.9.9-1.2 2.5-.5 3.5l2.5 3c1 .7 2.6.4 3.5-.5L15 15M6.5 17l2-2" />),
  Settings: svg(<><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 00.3 1.8l.1.1a2 2 0 11-2.8 2.8l-.1-.1a1.7 1.7 0 00-1.8-.3 1.7 1.7 0 00-1 1.5V21a2 2 0 01-4 0v-.1a1.7 1.7 0 00-1.1-1.5 1.7 1.7 0 00-1.8.3l-.1.1a2 2 0 11-2.8-2.8l.1-.1a1.7 1.7 0 00.3-1.8 1.7 1.7 0 00-1.5-1H3a2 2 0 010-4h.1a1.7 1.7 0 001.5-1.1 1.7 1.7 0 00-.3-1.8l-.1-.1a2 2 0 112.8-2.8l.1.1a1.7 1.7 0 001.8.3H9a1.7 1.7 0 001-1.5V3a2 2 0 014 0v.1a1.7 1.7 0 001 1.5 1.7 1.7 0 001.8-.3l.1-.1a2 2 0 112.8 2.8l-.1.1a1.7 1.7 0 00-.3 1.8V9a1.7 1.7 0 001.5 1H21a2 2 0 010 4h-.1a1.7 1.7 0 00-1.5 1z" /></>),
  Logout: svg(<path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />),
  Menu: svg(<path d="M4 6.5h16M4 12h16M4 17.5h16" />),
  Inbox: svg(<path d="M22 12h-6l-2 3h-4l-2-3H2M5.5 5.1L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.5-6.9A2 2 0 0016.8 4H7.2a2 2 0 00-1.7 1.1z" />),
};

export type IconName = keyof typeof Icon;

/** Ein Icon über seinen Namen — für Konfigurationen wie Menü und Bereiche. */
export const IconByName: React.FC<IconProps & { name: IconName }> = ({ name, ...rest }) => {
  const Komponente = Icon[name];
  return <Komponente {...rest} />;
};
